import { Inject, Injectable } from '@angular/core';
import { Router, UrlTree } from '@angular/router';
import {
  CsHttpClientService,
  JwtService,
  LOGIN_ROUTE,
  NO_ACCESS,
  StorageService
} from '@cbuild/cs-common-data-services';
import {
  BehaviorSubject,
  map,
  Observable,
  ReplaySubject,
  take,
  tap,
  shareReplay,
  throwError
} from 'rxjs';
import { BaseUrlService } from '../base-url.service';
import { FirebaseMessagingService } from '../firebase-messaging.service';

export interface User {
  exp: number;
  iss: string;
  aud: string;
  uid: string;
  useremail: string;
  csp: string;
  csm: string;
  usp: string;
  usc: string;
  rf: string;
  vars: string;
  theme: string;
  type: string;
  [key: string]: string | number | null;
}

const USER_TOKEN: string = 'l_token';
const SELECTED_ORG: string = 's_org';

@Injectable({ providedIn: 'root' })
export class AuthenticationService {
  public _redirectURL: UrlTree | undefined;
  private _isAuthenticated = false;
  private _apiKey: string | null = null;
  public user$ = new ReplaySubject<User | null>(1);
  public $token = new BehaviorSubject<null | {
    token: string | null;
    apiKey: string | null;
  }>(null);
  private _user!: User | null;
  public $orgId = new ReplaySubject<string | null>(1);
  private systems!: Array<string>;
  private _token!: string | null;
  private _orgId!: string | null;
  private _orgSelectRoute = '/select-org';
  public $hasMultipleOrg = new ReplaySubject<boolean>(1);

  // CRITICAL FIX: Use shareReplay to prevent multiple refresh requests
  private _refreshTokenRequest$: Observable<void> | null = null;

  constructor(
    private router: Router,
    private storageService: StorageService,
    private jwtService: JwtService,
    @Inject(LOGIN_ROUTE) private loginRoute: string,
    @Inject(NO_ACCESS) private noAccessPage: string,
    private http: CsHttpClientService,
    private baseUrlService: BaseUrlService,
    private messagingService: FirebaseMessagingService
  ) {}

  public async initialize(): Promise<void> {
    try {
      const token = this.storageService.getItem<string>(USER_TOKEN);
      this._orgId = this.storageService.getItem<string>(SELECTED_ORG);

      console.log('Initializing authentication...');
      this.updateAuthentication(token);

      // FIXED: Don't auto-refresh during initialization if expired - let interceptor handle it
      this.listenForToken();
      this.listenForOrganization();
      console.log('Authentication initialization completed');
    } catch (error) {
      console.error('Authentication initialization failed:', error);
      this.logOut(false);
    }
  }

  public get authInitialized(): Observable<boolean> {
    return this.user$.pipe(map(() => this.isAuthenticated));
  }

  /**
   * sets jwt token
   * @param token jwt token
   */
  public setJWTToken(token: string): void {
    this.storageService.setItem(USER_TOKEN, token);
  }

  /**
   * Gets Current User Data
   */
  public getUser(): User | null {
    return this._user;
  }

  /**
   * Gets Current org
   */
  public get orgId(): string | null {
    return this._orgId;
  }

  /**
   * Clears LocalStorage Token value
   */
  public logOut(navigateToLogin = true): void {
    const rf = this._user ? this._user.rf : '';
    this.deleteFcmToken(this._apiKey);
    this.deleteRefreshToken(rf);
    this._isAuthenticated = false;
    this._refreshTokenRequest$ = null; // Clear any pending refresh requests
    this.storageService.removeItem(USER_TOKEN);
    this.storageService.removeItem(SELECTED_ORG);
    this.$token.next(null); // Clear token subject
    this.user$.next(null); // Clear user subject
    if (navigateToLogin) {
      this.navigateToLogin();
    }
  }

  private deleteFcmToken(apiKey: string | null): void {
    if (apiKey) {
      this.messagingService
        .deleteFcmToken(apiKey)
        .pipe(take(1))
        .subscribe(() => {
          this.storageService.removeItem('fcmToken');
        });
    }
  }

  /**
   * Checks if user is authenticated
   */
  public get isAuthenticated(): boolean {
    return this._isAuthenticated;
  }

  /**
   * gets jwt token
   */
  public getToken(): string | null {
    return this._token;
  }

  /**
   * Checks if JWT Token Expired
   *  @param threshold Threshold in `seconds`
   */
  public isUserExpired(threshold = 10): boolean {
    if (!this._user || !this._user.exp) {
      return true; // If no user or exp, consider expired
    }
    const instant = Math.floor(new Date().getTime() / 1000);
    return this._user.exp < instant + threshold;
  }

  /**
   * Checks if User has permission code
   * @param appCode Permission Code
   * @param requiredPermissionMask permission mask
   */
  public hasUserPermission(
    appCode: string,
    requiredPermissionMask: number
  ): boolean {
    // if the required permission is 0, it is free!
    if (requiredPermissionMask === 0) {
      return true;
    }

    // the scope claim is a space separated list like: "FMGT:255"  etc
    const lookupString: string = appCode.toUpperCase() + ':';
    const neededSystem: string | undefined = this.systems?.find(i =>
      i.toUpperCase().startsWith(lookupString)
    );
    if (!neededSystem) {
      return false;
    }

    // now look up the required permission against the number
    const userPermissions: number = parseInt(
      neededSystem.replace(lookupString, '').trim(),
      10
    );
    if (userPermissions) {
      return (userPermissions & requiredPermissionMask) > 0;
    }
    return false;
  }

  /**
   * Checks if module `permission` in token
   * @param permission module permission code
   */
  public hasModulePermission(permission: string): boolean {
    return !!this._user?.csm.includes(permission.toUpperCase());
  }

  public hasFeaturePermission(permission: string): boolean {
    return !!this._user?.usp.includes(permission.toUpperCase());
  }

  /**
   * Checks if product `permission` in token
   * @param permission product permission code
   */
  public hasProductPermission(permission: string): boolean {
    return !!this._user?.csp.includes(permission.toUpperCase());
  }

  public navigateToLogin(): void {
    this.router.navigate([this.loginRoute]);
  }

  public navigateToSelectOrg(): void {
    this.router.navigate([this._orgSelectRoute]);
  }

  public navigateToHomePage(): void {
    let homePage = this.baseUrlService.getHomeBaseURL() || '';
    this.router.navigate([homePage]);
  }

  public navigateToNoAccess(): void {
    this.router.navigate([this.noAccessPage]);
  }

  /**
   * Parses and updates the JWT Token
   * @param jwt jwt token
   */
  private updateAuthentication(jwt: string): void {
    let obj: User | null = null;
    try {
      if (jwt) {
        obj = this.jwtService.decodeToken(jwt) as User;
        if (obj) {
          this.validateToken(obj);
          this.parseTokenPayload(obj);
          this.setOrgIfUserHasOne(obj.vars);
          const homeUrl = this.parseHomeUrl(obj.vars);
          this.baseUrlService.setHomeBaseUrl(homeUrl);
        }
      }
    } catch (e) {
      console.error('Token validation failed:', e);
      obj = null;
    } finally {
      this._isAuthenticated = !!obj?.uid;
      this._token = this._isAuthenticated ? jwt : null;
      this.setToken(obj?.vars, this._orgId, this._token);
      this.setUser(obj);
    }
  }

  private setOrgIfUserHasOne(vars: string): void {
    if (!vars) return;

    const allVars = vars.split(',');
    const conVars = allVars.filter(x => x.includes('CNKY'));
    if (conVars.length > 0) {
      this._orgId = conVars[0].split(':')[0];
      this.storageService.setItem(SELECTED_ORG, this._orgId);
    }
  }

  private setToken(
    vars: string | undefined,
    orgId: string | null,
    token: string | null
  ): void {
    if (!this._isAuthenticated || !vars || !orgId) {
      this.$token.next(null);
      return;
    }

    const lookUpString = `${orgId}:CNKY:`;
    const reqVar = vars.split(',').find(x => x.startsWith(lookUpString));
    this._apiKey = reqVar ? reqVar.replace(lookUpString, '') : null;

    const tokenData = { token, apiKey: this._apiKey };
    this.$token.next(tokenData);
  }

  private setUser(_user: User | null): void {
    this._user = _user;
    this.user$.next(_user);
  }

  /**
   * Active Token change listener on `LocalStorageService`
   */
  private listenForToken(): void {
    this.storageService.getItem$<string>(USER_TOKEN).subscribe(token => {
      this.updateAuthentication(token);
    });
  }

  /**
   * Active Token change listener on `LocalStorageService`
   */
  private listenForOrganization(): void {
    this.storageService.getItem$<string>(SELECTED_ORG).subscribe(orgId => {
      this._orgId = orgId;
      this.$orgId.next(this._orgId);
      // Update token with new org info
      if (this._user?.vars && this._token) {
        this.setToken(this._user.vars, orgId, this._token);
      }
    });
  }

  /**
   * Validates is token payload has `usp` Object
   * @param tokenObj token Payload
   */
  private validateToken(tokenObj: User): void {
    if (!tokenObj.usp) {
      throw new Error('CSuite Token no permissions in token');
    }
  }

  /**
   * Parses toke `usp`
   * @param tokenObj token Payload
   */
  private parseTokenPayload(tokenObj: User): void {
    this.systems = tokenObj.usp.split(',');
    if (this.systems.length === 0) {
      throw new Error('CSuite Token no application codes specified in token');
    }
  }

  private parseHomeUrl(vars: string): string {
    if (!vars || !this._orgId) return '/';

    const lookUpString = `${this._orgId}:HURI:`;
    const reqVar = vars.split(',').find(x => x.startsWith(lookUpString));
    return reqVar ? reqVar.replace(lookUpString, '') : '/';
  }

  public refreshToken(): Observable<void> {
    // Return cached request if already in progress
    if (this._refreshTokenRequest$) {
      console.log('Using cached refresh token request');
      return this._refreshTokenRequest$;
    }

    if (!this._user?.rf) {
      console.error('No refresh token found');
      return throwError(() => new Error('Refresh token not found'));
    }

    console.log('Creating new refresh token request');

    this._refreshTokenRequest$ = this.http
      .writeText<{ token: string }>(
        'api/master/refreshToken?service=1',
        this._user.rf,
        this.baseUrlService.getIdentityBaseURL()
      )
      .pipe(
        tap(response => {
          console.log('Refresh token response received');
          this.setJWTToken(response.token);
        }),
        map(() => void 0), // Convert to void
        shareReplay(1), // CRITICAL: Share the result to prevent multiple calls
        tap({
          complete: () => {
            console.log('Clearing cached refresh token request');
            this._refreshTokenRequest$ = null;
          },
          error: error => {
            console.error('Refresh token failed:', error);
            this._refreshTokenRequest$ = null;
            // Don't auto-logout here, let the interceptor handle it
          }
        })
      );

    return this._refreshTokenRequest$;
  }

  public deleteRefreshToken(rf: string): void {
    if (this.isAuthenticated && rf) {
      const sub = this.http
        .writeText<string>(
          'api/account/user/access-logout',
          rf,
          this.baseUrlService.getIdentityBaseURL()
        )
        .pipe(take(1))
        .subscribe({
          next: () => {
            console.log('Refresh token deleted successfully');
          },
          error: error => {
            console.error('Failed to delete refresh token:', error);
          },
          complete: () => {
            sub.unsubscribe();
          }
        });
    }
  }
}
